<template>
  <div class="login-page d-flex align-items-center justify-content-center">
    <div
      class="login-bg"
      :class="{ 'has-custom': hasCustomBg, 'hd-ready': bgHdReady }"
      :style="bgLayerStyle"
      aria-hidden="true"
    >
      <div class="login-bg-overlay"></div>
    </div>
    <div class="login-card">
      <template v-if="loading">
        <h1 class="page-title">加载中…</h1>
        <p class="page-subtitle text-muted">正在打开准考证登录页</p>
      </template>
      <template v-else-if="errorText">
        <h1 class="page-title">无法进入</h1>
        <p class="page-subtitle text-danger">{{ errorText }}</p>
      </template>
      <template v-else>
        <h1 class="page-title">{{ page.pageTitle || '准考证登录' }}</h1>
        <p class="page-subtitle">{{ page.pageSubtitle || '请输入准考证号完成登录' }}</p>

        <form @submit.prevent="handleSubmit" novalidate>
          <div class="mb-3 ticket-login-field">
            <label class="form-label">准考证号</label>
            <input
              v-model.trim="ticketNo"
              type="text"
              class="form-control custom-input"
              placeholder="请输入准考证号"
              autocomplete="off"
            />
            <small v-if="errorTicket" class="error-text">{{ errorTicket }}</small>
          </div>
          <button type="submit" class="btn login-btn w-100 mt-2" :disabled="submitting">
            {{ submitting ? '登录中…' : '登录' }}
          </button>
        </form>
        <div v-if="page.hintText" class="hint-text mt-3">{{ page.hintText }}</div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TicketLoginPublicView',
  data() {
    return {
      loading: true,
      submitting: false,
      errorText: '',
      errorTicket: '',
      token: '',
      ticketNo: '',
      bgHdReady: false,
      page: {
        pageTitle: '',
        pageSubtitle: '',
        hintText: '',
        bgImageUrl: '',
        bgImageJpgUrl: '',
        bgImageThumbUrl: ''
      }
    }
  },
  computed: {
    hasCustomBg() {
      const p = this.page || {}
      return !!(p.bgImageThumbUrl || p.bgImageUrl || p.bgImageJpgUrl)
    },
    bgLayerStyle() {
      if (!this.hasCustomBg) return {}
      const thumb = this.page.bgImageThumbUrl
      const webp = this.page.bgImageUrl
      const jpg = this.page.bgImageJpgUrl || webp
      if (!this.bgHdReady && thumb) {
        return {
          backgroundImage: `url("${this.cssUrl(thumb)}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat'
        }
      }
      const primary = webp || jpg
      const fallback = jpg || webp
      if (!primary) return {}
      const primaryIsWebp = /\.webp(\?|$)/i.test(String(primary))
      const imageSet = primaryIsWebp
        ? `image-set(url("${this.cssUrl(primary)}") type("image/webp"), url("${this.cssUrl(fallback)}") type("image/jpeg"))`
        : `url("${this.cssUrl(fallback)}")`
      return {
        backgroundImage: primaryIsWebp ? `${imageSet}, url("${this.cssUrl(fallback)}")` : imageSet,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      }
    }
  },
  created() {
    const hash = String(window.location.hash || '')
    const token = hash.startsWith('#/ticket-login/')
      ? hash.slice('#/ticket-login/'.length).split('?')[0].trim()
      : String((this.$route && this.$route.params && this.$route.params.token) || '').trim()
    this.token = token
    if (!token) {
      this.loading = false
      this.errorText = '登录地址无效'
      return
    }
    try {
      localStorage.setItem('exam_last_ticket_login_hash', `#/ticket-login/${token}`)
    } catch (e) {
      void e
    }
    fetch(`/api/ticket-login/open/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((res) => {
        if (res.code !== 200) {
          this.errorText = res.message || '登录地址无效或已关闭'
          return
        }
        this.page = res.data || {}
        this.preloadHdBackground()
      })
      .catch(() => {
        this.errorText = '网络异常，请稍后重试'
      })
      .finally(() => {
        this.loading = false
      })
  },
  methods: {
    cssUrl(u) {
      return String(u || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    },
    preloadHdBackground() {
      if (!this.hasCustomBg) {
        this.bgHdReady = false
        return
      }
      const webp = this.page.bgImageUrl
      const jpg = this.page.bgImageJpgUrl || webp
      const tryLoad = (url) =>
        new Promise((resolve) => {
          if (!url) {
            resolve(false)
            return
          }
          const img = new Image()
          img.decoding = 'async'
          img.onload = () => resolve(true)
          img.onerror = () => resolve(false)
          img.src = url
        })
      // 先尝试 WebP，失败则 JPG
      tryLoad(webp).then((ok) => {
        if (ok) {
          this.bgHdReady = true
          return
        }
        return tryLoad(jpg).then((ok2) => {
          if (ok2) this.bgHdReady = true
        })
      })
    },
    handleSubmit() {
      this.errorTicket = ''
      if (!this.ticketNo) {
        this.errorTicket = '请输入准考证号'
        return
      }
      this.submitting = true
      fetch(`/api/ticket-login/open/${encodeURIComponent(this.token)}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': '1'
        },
        body: JSON.stringify({ ticketNo: this.ticketNo })
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '登录失败')
            return
          }
          const user = res.data || {}
          const role = String(user.role || '').trim().toLowerCase()
          if (role !== 'student') {
            window.alert('该准考证未绑定学生账号')
            return
          }
          localStorage.setItem('exam_user', JSON.stringify(user))
          window.dispatchEvent(new Event('auth-changed'))
          window.dispatchEvent(new Event('exam-login-success-toast'))
          window.location.hash = '#/student/home'
        })
        .catch(() => {
          window.alert('网络异常，请检查后端服务是否已启动')
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped>
.login-page {
  --sky-50: #f0f9ff;
  --sky-100: #e0f2fe;
  --sky-500: #0ea5e9;
  --sky-600: #0284c7;
  --slate-500: #64748b;
  --slate-800: #1e293b;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  padding: clamp(20px, 5vw, 48px);
  overflow: hidden;
  background: #f8fafc;
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', system-ui, sans-serif;
}

.login-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(circle at 20% 20%, rgba(14, 165, 233, 0.16), transparent 42%),
    radial-gradient(circle at 80% 70%, rgba(56, 189, 248, 0.14), transparent 40%),
    linear-gradient(180deg, #f8fafc 0%, #eef6ff 100%);
  transition: filter 0.35s ease, opacity 0.35s ease;
}

.login-bg.has-custom {
  filter: blur(0);
}

.login-bg.has-custom:not(.hd-ready) {
  filter: blur(8px);
  transform: scale(1.04);
}

.login-bg-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.28) 0%, rgba(15, 23, 42, 0.42) 100%);
  opacity: 0;
}

.login-bg.has-custom .login-bg-overlay {
  opacity: 1;
}

.login-card {
  position: relative;
  z-index: 1;
  width: min(420px, 100%);
  padding: 28px 24px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #d7e6ff;
  box-shadow: 0 18px 48px rgba(35, 72, 150, 0.12);
}

.page-title {
  font-size: clamp(1.35rem, 3vw, 1.6rem);
  color: var(--slate-800);
  margin-bottom: 0.35rem;
  font-weight: 700;
}

.page-subtitle {
  color: var(--slate-500);
  margin-bottom: 1.25rem;
  font-size: 0.95rem;
}

.ticket-login-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  margin: 0;
  color: #334155;
  font-size: 0.875rem;
  font-weight: 600;
}

.custom-input {
  min-height: 42px;
  border-color: #cfe0ff !important;
  background: #fff !important;
  padding: 0.55rem 0.75rem !important;
}

.login-btn {
  background: var(--sky-600);
  border-color: var(--sky-600);
  color: #fff;
}

.login-btn:hover {
  background: var(--sky-500);
  border-color: var(--sky-500);
  color: #fff;
}

.error-text {
  color: #dc2626;
}

.hint-text {
  color: var(--slate-500);
  font-size: 0.875rem;
  white-space: pre-wrap;
  line-height: 1.5;
}
</style>
